import { useEffect, useRef, useState } from 'react';
import styles from './AudioPlayButton.module.css';
import { playExclusive } from '../../utils/exclusiveAudio';

/** 尺の表示: 短い音声は小数1桁の秒、長めは分:秒。 */
export function formatAudioDuration(seconds: number | null | undefined): string {
  if (seconds == null || !isFinite(seconds) || seconds <= 0) return '–';
  if (seconds < 10) return `${seconds.toFixed(1)}秒`;
  if (seconds < 60) return `${Math.round(seconds)}秒`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** 音声メタデータから尺 (秒) を取得する。取得できなければ null。 */
export function probeAudioDuration(src: string): Promise<number | null> {
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.src = src;
    audio.onloadedmetadata = () =>
      resolve(isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null);
    audio.onerror = () => resolve(null);
  });
}

/**
 * 音声をダウンロードする。data: URL は直接、http(s) は blob 化して
 * download 属性を効かせる (CORS で fetch できない場合は新規タブで開く)。
 */
export async function downloadAudio(src: string, name: string): Promise<void> {
  if (src.startsWith('data:')) {
    const a = document.createElement('a');
    a.href = src;
    a.download = name;
    a.click();
    return;
  }
  try {
    const resp = await fetch(src);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch {
    window.open(src, '_blank', 'noopener');
  }
}

interface AudioPlayButtonProps {
  src: string;
  className?: string;
  /** 再生 / 停止の切り替わりを呼び出し側へ伝える (AudioTrackTable が再生中の行を強調するのに使う)。 */
  onPlayingChange?: (playing: boolean) => void;
}

/**
 * 再生/停止だけの円形プレイボタン (シナリオ編集画面のボイス試聴ボタンと同じ見た目)。
 * 再生中は音声の再生に連動してパルス点滅する。秒数やダウンロードなどの周辺要素は
 * 呼び出し側でテキスト/アイコンとして並べる。
 */
export function AudioPlayButton({ src, className, onPlayingChange }: AudioPlayButtonProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const onPlayingChangeRef = useRef(onPlayingChange);
  useEffect(() => {
    onPlayingChangeRef.current = onPlayingChange;
  });

  useEffect(() => {
    onPlayingChangeRef.current?.(playing);
  }, [playing]);

  const ensureAudio = () => {
    if (!audioRef.current) {
      const audio = document.createElement('audio');
      audio.preload = 'none';
      audio.src = src;
      audio.onplay = () => setPlaying(true);
      audio.onpause = () => setPlaying(false);
      audio.onended = () => setPlaying(false);
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  // src が変わったら破棄 (アンマウント時も停止)
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, [src]);

  const togglePlay = () => {
    const audio = ensureAudio();
    if (audio.paused) {
      // 他の試聴 (素材ピッカー / BGM セクション等) を止めてから鳴らす
      void playExclusive(audio).catch(() => setPlaying(false));
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  };

  return (
    <button
      type="button"
      className={[styles.playButton, playing ? styles.playing : '', className]
        .filter(Boolean)
        .join(' ')}
      onClick={togglePlay}
      title={playing ? '停止' : '再生'}
      aria-label={playing ? '停止' : '再生'}
    >
      <AudioPlayGlyph playing={playing} />
    </button>
  );
}

/** 再生 / 停止の円形グリフ (16px)。AudioPlayButton と区間再生ボタン (audioTrim/AudioRangeTrimmer) で共用。 */
export function AudioPlayGlyph({ playing }: { playing: boolean }) {
  return playing ? (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <rect x="5.5" y="5.5" width="5" height="5" rx="0.5" fill="currentColor" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <polygon points="6.5,4.5 12,8 6.5,11.5" fill="currentColor" />
    </svg>
  );
}
